import { changeCase, isAPICustomError, isAPIValidationError } from '@frontend/duck-tape';
import type { GiftTaskFormSchema } from '@frontend/forms';
import { useGiftTaskForm } from '@frontend/forms';
import { useEffect, useState } from '@frontend/react';
import { Button, Icon, Modal, Text, TextInput, TextareaInput, XStack, YStack, showToast } from '@frontend/web-react';
import { useAnalytics, useGiftTaskMutation, useRetrieveClientQuery } from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';
import { GiftTaskSuccess } from './GiftTaskSuccess';

const GENERIC_ERROR_COPY = "Couldn't send gift. Try again.";

type GiftTaskModalProps = {
  benefits: string[];
  ctaLabel: string;
  errorCopy?: string;
  heading: string;
  isOpen: boolean;
  onClose: () => void;
  subheading: string;
};

export const GiftTaskModal = ({
  benefits,
  ctaLabel,
  errorCopy = GENERIC_ERROR_COPY,
  heading,
  isOpen,
  onClose,
  subheading,
}: GiftTaskModalProps) => {
  const { track } = useAnalytics();
  const { data: client } = useRetrieveClientQuery();
  const [giftTask, { isLoading: isSubmitting }] = useGiftTaskMutation();

  const [stage, setStage] = useState<'form' | 'success'>('form');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { getControl, handleSubmit, resetForm, setFieldError, submitCount, values } = useGiftTaskForm({
    onSubmit: async (formValues) => {
      const recipientEmail = formValues.recipientEmail.trim();
      const taskDescription = formValues.taskDescription.trim();

      if (client?.email && recipientEmail.toLowerCase() === client.email.trim().toLowerCase()) {
        setFieldError('recipientEmail', "You can't gift a task to yourself.");
        return;
      }

      setErrorMessage(null);
      const hasDescription = taskDescription.length > 0;
      track({ data: { hasDescription }, event: 'giftSubmitted', topic: 'gifts' });

      await handleMutation({
        mutation: () => giftTask({ recipientEmail, taskDescription }).unwrap(),
        onError: (error) => {
          if (isAPIValidationError(error)) {
            error.data.errors.forEach(({ field: apiField, message: fieldMessage }) => {
              const field = changeCase(apiField, 'camelCase') as keyof GiftTaskFormSchema;
              if (field === 'recipientEmail' || field === 'taskDescription') {
                setFieldError(field, fieldMessage);
              }
            });
            const message = error.data.errors[0]?.message ?? errorCopy;
            setErrorMessage(message);
            showToast({ title: message, variant: 'danger' });
            return;
          }
          const message = isAPICustomError(error) && error.status < 500 ? error.data.detail : errorCopy;
          setErrorMessage(message);
          showToast({ title: message, variant: 'danger' });
        },
        onSuccess: () => {
          track({ data: { hasDescription }, event: 'giftSucceeded', topic: 'gifts' });
          setSubmittedEmail(recipientEmail);
          setStage('success');
          resetForm();
        },
        showErrorToast: false,
        showSuccessToast: false,
      });
    },
  });

  useEffect(() => {
    if (isOpen) {
      resetForm();
      setStage('form');
      setSubmittedEmail('');
      setErrorMessage(null);
    }
  }, [isOpen]);

  const emailControl = getControl('recipientEmail');
  const descriptionControl = getControl('taskDescription');
  const showErrors = submitCount > 0;

  const shouldConfirmOnClose =
    stage === 'form' && !isSubmitting && (values.recipientEmail.length > 0 || values.taskDescription.trim().length > 0);

  return (
    <Modal
      confirmOnClose={shouldConfirmOnClose}
      contentBorderRadius="rounded-lg"
      isOpen={isOpen}
      onClose={onClose}
      padding="none"
      size="720px"
    >
      <div className="relative flex flex-col md:flex-row">
        <Icon
          accessibilityLabel="Close"
          className="absolute top-md right-md z-10"
          color="secondary"
          name="IconX"
          onClick={onClose}
          size="md"
        />

        <YStack
          className="w-full md:w-2/5 p-xl gap-lg"
          style={{
            background: 'linear-gradient(180deg, #F8FBCD 0%, #E5EDF7 71%, #E5EDF7 100%)',
            minHeight: 128,
          }}
        >
          <YStack className="gap-sm">
            <Text type="h2">{heading}</Text>
            <Text color="secondary" type="p-md">
              {subheading}
            </Text>
          </YStack>
          <YStack className="gap-sm hidden md:flex">
            {benefits.map((benefitBullet) => (
              <XStack className="items-center gap-sm" key={benefitBullet}>
                <Icon color="primary" name="IconCheck" size="sm" />
                <Text type="p-sm">{benefitBullet}</Text>
              </XStack>
            ))}
          </YStack>
        </YStack>

        <YStack className="w-full p-xl gap-md md:w-3/5">
          {stage === 'form' ? (
            <>
              {errorMessage ? (
                <XStack className="bg-red-50 border-1 border-red-200 rounded-md p-md items-center gap-sm" role="alert">
                  <Icon className="shrink-0" color="danger" name="IconAlertCircle" size="lg" />
                  <Text color="danger" type="p-sm">
                    {errorMessage}
                  </Text>
                </XStack>
              ) : null}

              <TextInput
                {...emailControl}
                autoFocus
                disabled={isSubmitting}
                error={showErrors ? emailControl.error : undefined}
                label="Recipient's email"
                placeholder="friend@example.com"
                type="email"
              />

              <TextareaInput
                {...descriptionControl}
                disabled={isSubmitting}
                error={showErrors ? descriptionControl.error : undefined}
                label="What's the task?"
                maxRows={8}
                minRows={3}
                placeholder="What should we help with?"
              />

              <Button
                disabled={isSubmitting}
                fullWidth
                isLoading={isSubmitting}
                onClick={() => handleSubmit()}
                variant="primary"
              >
                {isSubmitting ? 'Sending…' : ctaLabel}
              </Button>
            </>
          ) : (
            <GiftTaskSuccess
              onAnother={() => {
                setStage('form');
                setErrorMessage(null);
                resetForm();
              }}
              onDone={onClose}
              recipientEmail={submittedEmail}
            />
          )}
        </YStack>
      </div>
    </Modal>
  );
};
